import { Dayjs } from 'dayjs';
import { Company, Loan, Report, ReportParticipation, TCompanyId, TLoanId, TReportId, User } from '/src/lib/models';
import { ISODateTimeString } from '/src/lib/types';

/**
 * Reports
 */
export type AssetsPaymentsReport = {
  //
};

export type CashFlowReport = {
  payments: number;
  advances: number;
};

export type LoanBalancesReport = {
  //
};

export enum MonthlyActivityCategory {
  Advances = 'advances',
  Revenue = 'revenue',
  PrincipalPayments = 'principalPayments',
}

export type MonthlyActivityReportItem<T = MonthlyActivityCategory> = {
  category: T;
  company: string; // company name
  loan: string; // loan name
  month: Dayjs; // YYYY-MM
  value: number;
  lenders: string[]; // lender names
};

export type MonthlyActivityReport = {
  month: Dayjs; // YYYY-MM
  advances: number;
  revenue: number;
  principalPayments: number;
  items: {
    advances: MonthlyActivityReportItem<MonthlyActivityCategory.Advances>[];
    revenue: MonthlyActivityReportItem<MonthlyActivityCategory.Revenue>[];
    principalPayments: MonthlyActivityReportItem<MonthlyActivityCategory.PrincipalPayments>[];
    [key: string]: MonthlyActivityReportItem<MonthlyActivityCategory>[];
  };
};

export type RevenueReport = {
  amount: number;
  company: string;
  chartTitle: string;
};

/**
 * Get Monthly Activity Report
 */
export type TGetMonthlyActivityReportRequest = {
  start: ISODateTimeString;
  end: ISODateTimeString;
  loanId?: TLoanId;
  lenderId?: TCompanyId;
  companyId?: TCompanyId;
};

export type TGetMonthlyActivityReportResponse = MonthlyActivityReport[];

/**
 * Get Loan Balances Report
 */
export type TGetLoanBalancesReportRequest = {
  lenderId?: TCompanyId;
};

export type TGetLoanBalancesReportResponse = LoanBalancesReport[];

/**
 * Get Revenue Report
 */
export type TGetRevenueReportRequest = {
  lenderId?: TCompanyId;
};

export type TGetRevenueReportResponse = RevenueReport[];

/**
 * Get Cash Flow Report
 */
export type TGetCashFlowReportRequest = {
  lenderId?: TCompanyId;
};

export type TGetCashFlowReportResponse = CashFlowReport[];

/**
 * Get Asset & Payments Report
 */
export type TGetAssetsPaymentsReportRequest = {
  lenderId?: TCompanyId;
};

export type TGetAssetsPaymentsReportResponse = AssetsPaymentsReport[];

/**
 * Generate Report
 */
export type TGenerateReportRequest = Record<string, any>;

export type TGenerateReportResponse = any[];

/**
 * List Reports
 */
export type TListReportsRequest = Record<string, any>;

export type TListReportsResponse = Report[];

/**
 * Get Report
 */
export type TGetReportResponse = any[];

/**
 * Create Report
 */
export type TCreateReportRequest = {
  // Calculated txn fields
  appliedToFees?: number;
  appliedToInterest?: number;
  appliedToPrincipal?: number;

  // Calculated balance fields
  principalBalance?: number;
  feeBalance?: number;
  interestBalance?: number;
  assetValue?: number;

  // Transaction Fields
  __t?: string;
  name?: string; // Default to '' if not provided
  created?: Date; // Defaults to Date.now
  sequenceNumber: number; // Required
  effectiveDate: Date; // Required
  user?: User; // Reference to 'User'
  company: Company; // Reference to 'Company' (required)
  amount: number; // Required
  loan: Loan; // Reference to 'Loan' (required)
  assetId?: string;

  availableCredit?: number;
  totalAvailableCredit?: number;
  interestCharge?: number;

  // Fees
  diligenceFee?: number; // Default to 0
  facilityFee?: number; // Default to 0
  adminFee?: number; // Default to 0
  originationFee?: number; // Default to 0
  lateFee?: number; // Default to 0
  discountFee?: number; // Default to 0

  // Convenience Fields
  companyName?: string;
  loanName?: string;
  assetName?: string;

  // For Statements
  isDaily?: boolean;
  minimumPayment?: number;

  // Calculated Participation
  participation?: ReportParticipation[]; // Array of participation details
};

export type TCreateReportResponse = any[];

/**
 * Update Report
 */
export type TUpdateReportRequest = {
  id: TReportId;

  // Calculated txn fields
  appliedToFees?: number;
  appliedToInterest?: number;
  appliedToPrincipal?: number;

  // Calculated balance fields
  principalBalance?: number;
  feeBalance?: number;
  interestBalance?: number;
  assetValue?: number;

  // Transaction Fields
  __t?: string;
  name?: string; // Default to '' if not provided
  created?: Date; // Defaults to Date.now
  sequenceNumber: number; // Required
  effectiveDate: Date; // Required
  user?: User; // Reference to 'User'
  company: Company; // Reference to 'Company' (required)
  amount: number; // Required
  loan: Loan; // Reference to 'Loan' (required)
  assetId?: string;

  availableCredit?: number;
  totalAvailableCredit?: number;
  interestCharge?: number;

  // Fees
  diligenceFee?: number; // Default to 0
  facilityFee?: number; // Default to 0
  adminFee?: number; // Default to 0
  originationFee?: number; // Default to 0
  lateFee?: number; // Default to 0
  discountFee?: number; // Default to 0

  // Convenience Fields
  companyName?: string;
  loanName?: string;
  assetName?: string;

  // For Statements
  isDaily?: boolean;
  minimumPayment?: number;

  // Calculated Participation
  participation?: ReportParticipation[]; // Array of participation details
};

export type TUpdateReportResponse = any[];

/**
 * Delete Report
 */
export type TDeleteReportResponse = void;
